// ---------------------------------------------------------------------------
// usePlanogramLock
//
// Extracted from PlanogramDetails.tsx: lockInfo/isReadOnlyMode state, the
// PlanogramLockRefresher instantiation/start/destroy in the init effect's
// setup+cleanup, and the unlockPlanogram call on unmount.
//
// This hook owns the lock's entire lifecycle: starts the periodic refresh
// timer when a planogramId is provided (edit mode), reads the initial
// lock state from whatever the fetch response says, and tears everything
// down (stop refreshing, release the lock) on unmount — exactly the original
// cleanup behavior, just no longer interleaved with the rest of
// PlanogramDetails's init effect.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import PlanogramLockRefresher from '../../../../utils/lock/PlanogramLockRefresher';
import UserEntityLockService from '../../../../service/UserEntityLockService';

export interface LockInfo {
    lockedBy: string;
}

export interface PlanogramLockApiResult {
    blockedByAnotherUser?: boolean;
    lockedByUser?: { userName?: string };
}

export function usePlanogramLock(planogramId: string | undefined, isEditMode: boolean, refreshIntervalMs = 15000) {
    const [lockInfo, setLockInfo] = useState<LockInfo | null>(null);
    const [isReadOnlyMode, setIsReadOnlyMode] = useState(false);
    const refresherRef = useRef<PlanogramLockRefresher | null>(null);

    /** Call once the initial fetch resolves, with whatever lockResult the API returned. */
    const applyLockResult = (lockResult: PlanogramLockApiResult | undefined) => {
        if (lockResult?.blockedByAnotherUser) {
            setLockInfo({ lockedBy: lockResult.lockedByUser?.userName ?? 'another user' });
            setIsReadOnlyMode(true);
        } else {
            setLockInfo(null);
            setIsReadOnlyMode(false);
        }
    };

    useEffect(() => {
        if (!isEditMode || !planogramId) return;

        refresherRef.current = new PlanogramLockRefresher({
            planogramId,
            refresh: async () => {
                await UserEntityLockService.refreshPlanogramLock(planogramId);
            },
            intervalMs: refreshIntervalMs,
            logger: console,
        });
        refresherRef.current.start();

        return () => {
            refresherRef.current?.destroy();
            refresherRef.current = null;

            UserEntityLockService.unlockPlanogram(planogramId).catch((error: unknown) => {
                console.error('Error unlocking planogram:', error);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, planogramId]);

    return {
        lockInfo,
        isReadOnlyMode,
        applyLockResult,
    };
}

export type UsePlanogramLockReturn = ReturnType<typeof usePlanogramLock>;